import { gateLayout } from './layout.js';
import { CONTENT } from '../content/runtime.js';
import { CONFIG } from './config.js';

export const PLANE_SIZE = CONTENT.balance.constants.PLANE_SIZE;
export const STAND_Y = CONTENT.balance.constants.STAND_Y;
export const RUNWAY_Y = CONTENT.balance.constants.RUNWAY_Y;
export const GROUND_FRAMES = CONTENT.balance.constants.GROUND_FRAMES;
type GateDoor = { x: number; y: number; door?: { x: number; y: number }; facing?: number[] };
/** A gate's doorway and the way its aircraft parks (out through the wall); the shared plan's gates face north. */
export function gateDoor(airportId: string, gate: number): { door: { x: number; y: number }; facing: [number, number] } {
    const g = gateLayout(airportId).GATES[gate] as GateDoor;
    return g.door && g.facing ? { door: g.door, facing: [g.facing[0], g.facing[1]] } : { door: { x: g.x + 70, y: 330 }, facing: [0, -1] };
}
/** How much further out a jet-bridge airport parks its aircraft, to leave room for the cab and tunnel. */
export const BRIDGE_STAND = 80;
/** And how much further out any aircraft parks, so its wing clears the terminal wall. */
export const WING_CLEARANCE = 40;
export function standReach(airportId: string) { return 330 - STAND_Y + WING_CLEARANCE + (boardingStyle(airportId) === 'bridge' ? BRIDGE_STAND : 0); }
/** Where a gate's aircraft parks; at a jet-bridge airport it pulls up with its door square in front of the gate door. */
export function aircraftStand(airportId: string, gate: number) {
    const { door, facing: [fx, fy] } = gateDoor(airportId, gate), reach = standReach(airportId);
    const back = boardingStyle(airportId) === 'bridge' ? doorAlong(airportId) : 0, [nx, ny] = parkedHeading(airportId, gate).nose;
    return { x: door.x + fx * reach - nx * back, y: door.y + fy * reach - ny * back };
}
export function departurePoint(airportId: string, gate: number) {
    const { door, facing: [fx, fy] } = gateDoor(airportId, gate);
    return { x: door.x - fx * 88 - fy * 45, y: door.y - fy * 88 + fx * 45 };
}
/** Airstairs out on the apron (small airports) or a covered jet bridge from the gate door (big ones). */
export function boardingStyle(airportId: string): 'stairs' | 'bridge' {
    return CONTENT.airports.find(a => a.code === airportId)?.boarding === 'bridge' ? 'bridge' : 'stairs';
}
/** The parked aircraft's forward door: how far toward the nose from the stand, and out from the centre line. */
export const DOOR_ALONG = 60, DOOR_SIDE = 42;
/** A jet bridge docks at the very front door, clear ahead of the wing. */
export const BRIDGE_DOOR_ALONG = 93;
export function doorAlong(airportId: string) { return boardingStyle(airportId) === 'bridge' ? BRIDGE_DOOR_ALONG : DOOR_ALONG; }
/** How far the foot of the airstairs stands out from the aircraft door. */
export const STAIRS_REACH = 78;
/**
 * A parked aircraft's nose and its side facing the terminal. It lies along the wall (nose east on a north wall), so a
 * jet bridge runs straight out to its door; only at an airstairs airport does it park nose-in at another wall.
 */
export function parkedHeading(airportId: string, gate: number): { nose: [number, number]; side: [number, number] } {
    const [fx, fy] = gateDoor(airportId, gate).facing, nose: [number, number] = fy < 0 || boardingStyle(airportId) === 'bridge' ? [-fy, fx] : [-fx, -fy];
    return { nose, side: [-nose[1], nose[0]] };
}
/** Where passengers step aboard a parked aircraft, and (airstairs) where the stairs meet the apron. */
export function aircraftDoor(airportId: string, gate: number) {
    const stand = aircraftStand(airportId, gate), { nose: [ax, ay], side: [sx, sy] } = parkedHeading(airportId, gate);
    const along = doorAlong(airportId), at = { x: stand.x + ax * along + sx * DOOR_SIDE, y: stand.y + ay * along + sy * DOOR_SIDE };
    return { at, foot: { x: at.x + sx * STAIRS_REACH, y: at.y + sy * STAIRS_REACH } };
}
/** World size of a jet bridge's cab (the boarding-bridge-head model), which fills the gate doorway, and its gap off the wall. */
export const BRIDGE_HEAD = 96, BRIDGE_WALL = 12;
/**
 * A jet bridge: its big cab fills the gate doorway just outside the wall, mouth to the terminal, and a narrow tunnel
 * runs straight out of its far face along the gate's facing (the model only turns in quarters) to the aircraft door.
 */
export function jetBridge(airportId: string, gate: number) {
    const { door, facing: [fx, fy] } = gateDoor(airportId, gate), { at } = aircraftDoor(airportId, gate);
    const lateral = (at.x - door.x) * -fy + (at.y - door.y) * fx, out = BRIDGE_WALL + BRIDGE_HEAD;
    return { head: { x: door.x + fx * (BRIDGE_WALL + BRIDGE_HEAD / 2), y: door.y + fy * (BRIDGE_WALL + BRIDGE_HEAD / 2) },
        from: { x: door.x - fy * lateral + fx * out, y: door.y + fx * lateral + fy * out }, to: at,
        mouth: [-fx, -fy] as [number, number], facing: [fx, fy] as [number, number] };
}
/**
 * Inside the gate door, the doorway, then out to the aircraft door: into the jet bridge's cab and along its tunnel, or
 * out onto the apron, across to the airstairs and up them. Separate lanes through the doorway keep boarding and arrivals from
 * walking head-on. Every point after the doorway is off the terminal floor, so it is walked straight, not navigated.
 */
export function gateWalk(airportId: string, gate: number, arriving = false) {
    const { door, facing: [fx, fy] } = gateDoor(airportId, gate), lane = arriving ? 18 : -18, sx = -fy * lane, sy = fx * lane;
    const plane = aircraftDoor(airportId, gate), inside = { x: door.x - fx * 60 + sx, y: door.y - fy * 60 + sy }, doorway = { x: door.x + sx, y: door.y + sy };
    if (boardingStyle(airportId) === 'bridge') { const b = jetBridge(airportId, gate); return [inside, doorway, b.head, b.from, plane.at]; }
    return [inside, doorway, { x: door.x + fx * 65 + sx, y: door.y + fy * 65 + sy }, plane.foot, plane.at];
}
/** How far up the airstairs a point is (0 at the foot, 1 at the aircraft door); 0 off them or at a jet-bridge airport. */
export function stairsProgress(airportId: string, gate: number, at: { x: number; y: number }) {
    if (boardingStyle(airportId) !== 'stairs') return 0;
    const { at: top, foot } = aircraftDoor(airportId, gate), dx = top.x - foot.x, dy = top.y - foot.y, len = Math.hypot(dx, dy);
    const along = ((at.x - foot.x) * dx + (at.y - foot.y) * dy) / len, across = Math.abs((at.x - foot.x) * dy - (at.y - foot.y) * dx) / len;
    return across < 12 && along > 0 ? Math.min(1, along / len) : 0;
}
/** An arriving passenger's first walkStep: they start at the aircraft door and count up to 1, the doorway-to-inside leg. */
export function arrivalStep(walk: readonly unknown[]) { return 3 - walk.length; }
export const WAITING_SEATS = CONTENT.layout.WAITING_SEATS;
export const EXTRA_SEATS = CONTENT.layout.EXTRA_SEATS;
export function waitingSeats(chairLevel: number, maxX: number) {
    return [...WAITING_SEATS, ...(chairLevel ? EXTRA_SEATS : [])].filter(s => s.x < maxX - 50);
}

/** The airfield north of every terminal: the runway strip, then the apron under the north wall (world y ranges). */
export const RUNWAY = { left: 24, right: CONFIG.width - 24, top: -150, bottom: 90 };
export const APRON = { top: 90, bottom: 330, centre: 210 };
/** How far a side gate's aircraft pushes back: its taxi line then passes a neighbouring stand on the same wall wingtip-clear. */
export const PUSHBACK = 280;
/** How far a takeoff or landing roll runs along the runway. */
export const ROLL = 900;

/**
 * Ground route from a gate's stand to the runway. A north-facing gate's stand is on the apron, so it taxis straight
 * on. A gate on any other wall pushes back out along its facing (PUSHBACK), taxis north to the apron, along it while the
 * runway does not reach that far, then onto the runway.
 */
export function taxiRoute(airportId: string, gate: number) {
    const stand = aircraftStand(airportId, gate), [fx, fy] = gateDoor(airportId, gate).facing;
    if (fy < 0) return [stand, stand, { x: stand.x, y: RUNWAY_Y }];
    const out = { x: stand.x + fx * PUSHBACK, y: stand.y + fy * PUSHBACK }, x = Math.max(RUNWAY.left + 100, Math.min(RUNWAY.right - 100, out.x));
    return x === out.x ? [stand, out, { x, y: RUNWAY_Y }] : [stand, out, { x: out.x, y: APRON.centre }, { x, y: APRON.centre }, { x, y: RUNWAY_Y }];
}
const lengthOf = (points: readonly { x: number; y: number }[]) => points.slice(1).reduce((sum, p, i) => sum + Math.hypot(p.x - points[i].x, p.y - points[i].y), 0);
/** Frames on the taxiway (a steady pace, 60 for a short straight push) and on the runway roll, which take GROUND_FRAMES between them. */
export function groundFrames(airportId: string, gate: number) {
    const taxi = Math.max(60, Math.min(150, Math.round(lengthOf(taxiRoute(airportId, gate)) / 3.5)));
    return { taxi, roll: GROUND_FRAMES - taxi };
}
/** Rolls head for the runway end with more room, so every roll stays on the runway. */
export function rollDirection(x: number) { return x < (RUNWAY.left + RUNWAY.right) / 2 ? 1 : -1; }
/** Takeoff distance from the runway entry after a fraction u of the roll: speeding up from taxi pace. */
const rolled = (u: number) => ROLL * (0.25 * u + 0.75 * u * u);
/**
 * Where an aircraft is `clock` frames into its ground phase and how high it has lifted (render units). A departure taxis
 * out and rolls away, lifting off over the last stretch; an arrival touches down, slows along the same stretch of
 * runway and taxis back in. Pure: the sim places the aircraft from it and the scene lifts the art.
 */
export function groundPose(airportId: string, gate: number, clock: number, landing: boolean) {
    const route = taxiRoute(airportId, gate), entry = route[route.length - 1], { taxi, roll } = groundFrames(airportId, gate), dir = rollDirection(entry.x);
    const runway = (u: number) => ({ x: entry.x + dir * rolled(u), y: entry.y, lift: Math.max(0, (u - 0.6) / 0.4) * 72 });
    if (!landing) return clock < taxi ? { ...along(route, clock / taxi), lift: 0 } : runway(Math.min(1, (clock - taxi) / roll));
    return clock < roll ? runway(1 - clock / roll) : { ...along([...route].reverse(), (clock - roll) / taxi), lift: 0 };
}
/** Position a fraction 0..1 along a polyline, by length. */
export function along(points: readonly { x: number; y: number }[], t: number) {
    const lengths = points.slice(1).map((p, i) => Math.hypot(p.x - points[i].x, p.y - points[i].y)), total = lengths.reduce((a, b) => a + b, 0);
    let d = Math.max(0, Math.min(1, t)) * total;
    for (let i = 0; i < lengths.length; i++) {
        if (d <= lengths[i] || i === lengths.length - 1) { const k = lengths[i] ? Math.min(1, d / lengths[i]) : 1; return { x: points[i].x + (points[i + 1].x - points[i].x) * k, y: points[i].y + (points[i + 1].y - points[i].y) * k }; }
        d -= lengths[i];
    }
    return { ...points[points.length - 1] };
}
